/**
 * Black Box — one-time DOM structure snapshot.
 *
 * Captures a compact, privacy-safe ELEMENT MAP (identity + semantics, NEVER
 * values) so Claude can resolve a recorded `el` to a real page element and read
 * its attributes — instead of relying on a hardcoded per-page description.
 *
 * PRIVACY INVARIANT (same as the event tracer): we read tag / type / label /
 * placeholder / autocomplete / role — page-authored chrome only. We NEVER read
 * `.value`. Must fail silent and never break the host page.
 */
import { elId, isRendered } from './util.js';

// Elements worth describing to the model: form controls + actionable elements.
// Decorative nodes (svg/img/plain text) are skipped to keep the map small.
const CONTROL_SEL = 'input, select, textarea, button, a[href], [role="button"]';
// Generic containers (wrapper divs, step panes, error boxes) worth naming IF they
// carry a stable identity. Elements without id/data-bb fall back to an ambiguous
// tag.class selector anyway (e.g. two different `div.card`s collide on the same
// string) — so only identity-bearing elements are included, to avoid attaching a
// label to the wrong element.
const GENERIC_SEL = '[id], [data-bb]';
const MAX_ELS = 80;
const MAX_GENERIC = 40; // sub-cap so generic containers can't crowd out real controls
const MAX_LABEL = 60;
// Steps whose content is user-entered data rendered back to the page (OTP/phone
// confirmations, success screens) — never text-scrape inside these.
const SENSITIVE_STEP = /confirm|success|complete|thank|otp|verify|review/i;

function clip(s) {
  try {
    const t = String(s || '').replace(/\s+/g, ' ').trim();
    return t ? t.slice(0, MAX_LABEL) : null;
  } catch (_) { return null; }
}

// Immediate text of an element, ignoring nested children — avoids descending
// into dynamically-rendered content, which normally lives inside child nodes.
function directText(el) {
  try {
    let t = '';
    for (const node of el.childNodes) {
      if (node.nodeType === 3) t += node.textContent;
    }
    t = t.replace(/\s+/g, ' ').trim();
    return t ? clip(t) : null;
  } catch (_) { return null; }
}

// Human label for a control — WITHOUT reading its value.
function labelFor(el, config) {
  try {
    if (el.getAttribute) {
      const aria = el.getAttribute('aria-label');
      if (aria) return clip(aria);
    }
    if (el.id) {
      const lbl = document.querySelector('label[for="' + el.id + '"]');
      if (lbl) return clip(lbl.textContent);
    }
    const wrap = el.closest && el.closest('label');
    if (wrap) return clip(wrap.textContent);
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'button' || tag === 'a') return clip(el.textContent);
    if (el.getAttribute) {
      const ph = el.getAttribute('placeholder');
      if (ph) return clip(ph);
    }
    // Generic container fallback — direct text only, and never inside a step
    // that renders user-entered data back (confirmation/success screens).
    if (!SENSITIVE_STEP.test(stepOf(el, config && config.steps) || '')) {
      const dt = directText(el);
      if (dt) return dt;
    }
  } catch (_) {}
  return null;
}

function hidden(el) {
  return !isRendered(el);
}

// Which configured step container this element lives in (name or null).
function stepOf(el, steps) {
  try {
    for (const name in (steps || {})) {
      const container = document.querySelector(steps[name]);
      if (container && container.contains(el)) return name;
    }
  } catch (_) {}
  return null;
}

function describe(el, config) {
  const rec = { sel: elId(el), tag: (el.tagName || '').toLowerCase() };
  try {
    if (el.getAttribute) {
      const type = el.getAttribute('type'); if (type) rec.type = type;
      const ac = el.getAttribute('autocomplete'); if (ac) rec.autocomplete = ac;
      const im = el.getAttribute('inputmode'); if (im) rec.inputmode = im;
      const role = el.getAttribute('role'); if (role) rec.role = role;
    }
    if (el.disabled) rec.disabled = true;
    const label = labelFor(el, config); if (label) rec.label = label;
    const step = stepOf(el, config && config.steps); if (step) rec.step = step;
    if (hidden(el)) rec.hidden = true;
  } catch (_) {}
  return rec;
}

/**
 * Build the element map for the current page. Includes form controls and the
 * configured error slots (divs the events reference by `field`) for context.
 */
export function snapshotDom(config) {
  const out = [];
  const seen = {};
  try {
    const nodes = document.querySelectorAll(CONTROL_SEL);
    for (let i = 0; i < nodes.length && out.length < MAX_ELS; i++) {
      const rec = describe(nodes[i], config);
      if (rec.sel && !seen[rec.sel]) { seen[rec.sel] = 1; out.push(rec); }
    }
    // configured error slots (usually hidden divs) — useful structure context
    const errs = (config && config.errors) || {};
    for (const field in errs) {
      if (out.length >= MAX_ELS) break;
      const node = document.querySelector(errs[field]);
      if (!node) continue;
      const rec = describe(node, config);
      rec.role = rec.role || 'error';
      if (rec.sel && !seen[rec.sel]) { seen[rec.sel] = 1; out.push(rec); }
    }
    // Generic containers (wrapper divs, step panes) that carry a stable id/
    // data-bb — these are what show up as raw selectors in the dashboard
    // since they're not form controls. Capped separately so they can't crowd
    // out real controls above.
    let genericCount = 0;
    const generic = document.querySelectorAll(GENERIC_SEL);
    for (let i = 0; i < generic.length && out.length < MAX_ELS && genericCount < MAX_GENERIC; i++) {
      const node = generic[i];
      if (node.matches && node.matches(CONTROL_SEL)) continue; // already covered above
      const rec = describe(node, config);
      if (rec.sel && !seen[rec.sel]) { seen[rec.sel] = 1; out.push(rec); genericCount++; }
    }
  } catch (_) {}
  return out;
}
